import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { Send, CheckCircle, Lock, PlayCircle, X, HelpCircle, Loader2 } from 'lucide-react';
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';
import { meetingHeroBank } from '../../../data/MeetingHeroBank';
import { interviewMasteryBank } from '../../../data/InterviewMasteryBank';

// NEW INJECTION: Load the 25-item hardcoded Emotional Intelligence bank
import { eqBank } from '../../../data/EmotionalIntelligenceBank';

const ChatHeroGame = ({ gameData, onComplete }) => {
  const { playSuccess, playError, playWin } = useGameSounds();

  const { 
    id, 
    preGameBriefing, 
    failureLogic     
  } = gameData || {};

  // UX Calibration: Stable initialization with Fisher-Yates array shuffle.
  const scenariosRef = useRef([]);

  if (scenariosRef.current.length === 0) {
      if (id === 'game_meeting_hero') {
          let array = [...meetingHeroBank];
          for (let i = array.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [array[i], array[j]] = [array[j], array[i]];
          }
          scenariosRef.current = array.slice(0, 5);
      } else if (id === 'game_interview_1' || id === 'game_behavioral_gauntlet') {
          let array = [...interviewMasteryBank];
          for (let i = array.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [array[i], array[j]] = [array[j], array[i]];
          }
          scenariosRef.current = array.slice(0, 3);
      } else if (id === 'game_eq_builder') {
          // MODULE 4 INJECTION: Dynamic 4-Item Array Loop for Emotional Intelligence
          let array = [...eqBank];
          for (let i = array.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [array[i], array[j]] = [array[j], array[i]];
          }
          scenariosRef.current = array.slice(0, 4);
      } else if (gameData?.scenarios) {
          scenariosRef.current = gameData.scenarios;
      } else {
          scenariosRef.current = [gameData];
      }
  }

  const activeScenarioList = scenariosRef.current;
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const activeScenario = activeScenarioList[scenarioIndex];

  const initialPool = useMemo(() => {
      if (!activeScenario) return [];
      
      if (activeScenario.options) {
          // Meeting Hero & Interview Mastery style - SHUFFLE INJECTED
          const shuffledOptions = [...activeScenario.options].sort(() => Math.random() - 0.5);
          
          return shuffledOptions.map((opt, i) => ({
              id: `chip_${i}`,
              text: opt.text || opt.responseText, 
              category: opt.category,
              feedback: opt.feedback
          }));
      }
      
      // EQ Builder & One-touch style - SHUFFLE INJECTED
      return activeScenario.availableChips 
        ? [...activeScenario.availableChips].sort(() => Math.random() - 0.5).map((text, i) => ({ id: `chip_${i}`, text }))
        : (activeScenario.fragments ? [...activeScenario.fragments].sort(() => Math.random() - 0.5) : []);
  }, [activeScenario]);

  const [pool, setPool] = useState(initialPool); 
  const [draft, setDraft] = useState([]); 
  const [gameStatus, setGameStatus] = useState('briefing'); // 'briefing', 'playing', 'won', 'intervention'
  const [bossMessage, setBossMessage] = useState("");
  const [activeIntervention, setActiveIntervention] = useState(null);
  const [sessionAttempts, setSessionAttempts] = useState([]);
  
  // UX Calibration: Hint System
  const [hasFailedOnce, setHasFailedOnce] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ADA Compliance: Silent telemetry background tracker
  const sessionStartRef = useRef(Date.now());
  const interactionStartTimeRef = useRef(null); 
  
  // TELEMETRY: Assembly Deconstruction Volatility
  const volatilityEventsRef = useRef(0);

  const bossName = activeScenario?.recipient || activeScenario?.bossName || "Unknown";
  const bossAvatar = activeScenario?.recipient ? "👤" : (activeScenario?.bossAvatar || "📹");

  // --- 1. HANDLE SCENARIO MOUNT ---
  useEffect(() => {
    if (preGameBriefing && scenarioIndex === 0) {
        setGameStatus('briefing');
    } else {
        setGameStatus('playing');
        sessionStartRef.current = Date.now();
        interactionStartTimeRef.current = Date.now();
        volatilityEventsRef.current = 0;
    }
  }, [preGameBriefing, scenarioIndex]);

  useEffect(() => {
    setPool(initialPool);
    setDraft([]);
    // Dynamically pull the correct prompt text based on the active scenario schema
    setBossMessage(activeScenario?.scenarioContext || activeScenario?.leaderPrompt || activeScenario?.adversaryPrompt || activeScenario?.incomingMessage || activeScenario?.initialMessage || "");
  }, [activeScenario, initialPool]);

  if (!gameData || !activeScenario) return <div className="text-red-500">Error: Missing Game Data</div>;

  // --- 2. FAILURE LOGIC ROUTER ---
  const triggerFailure = (reasonCode, customScript = null) => {
      setHasFailedOnce(true);
      setGameStatus('intervention'); 
      const logic = failureLogic?.[reasonCode] || {
          title: "Protocol Violation",
          script: customScript || gameData.failMessage || "Your message broke protocols. Review your syntax.",
          videoUrl: null 
      };
      if (customScript) logic.script = customScript;
      
      setActiveIntervention(logic);
      setBossMessage("..."); 
      playError();
  };

  const handleMoveToDraft = (item) => {
    if (gameStatus !== 'playing') return;
    if ((id === 'game_meeting_hero' || id === 'game_interview_1' || id === 'game_behavioral_gauntlet') && draft.length >= 1) return; // Enforce single selection
    playSuccess(); 
    setPool(pool.filter(i => i.id !== item.id));
    setDraft([...draft, item]);
  };

  const handleMoveToPool = (item) => {
    if (gameStatus !== 'playing') return;
    playSuccess(); 

    // TELEMETRY SENSOR: Assembly Deconstruction Volatility
    // If the user drafted a trap chip and then deleted it, we log the hesitation.
    if (id === 'game_eq_builder' && activeScenario?.optimalSequence) {
        if (!activeScenario.optimalSequence.includes(item.text)) {
            volatilityEventsRef.current += 1;
        }
    }

    setDraft(draft.filter(i => i.id !== item.id));
    setPool([...pool, item]);
  };

  // --- 3. CONTEXTUAL CHECK LOGIC ---
  const handleSend = () => {
    if (gameStatus !== 'playing') return;
    const userSequence = draft.map(d => d.text);
    const interactionLatency = Date.now() - interactionStartTimeRef.current;

    // EQ BUILDER MODE (Module 4)
    if (id === 'game_eq_builder') {
        const isCorrect = JSON.stringify(userSequence) === JSON.stringify(activeScenario.optimalSequence);

        setSessionAttempts(prev => [...prev, { 
            status: isCorrect ? 'success' : 'failed', 
            latencyMs: interactionLatency, 
            sequence: userSequence,
            volatilityCount: volatilityEventsRef.current
        }]);

        if (isCorrect) {
            setHasFailedOnce(false);
            setShowHint(false);
            if (scenarioIndex + 1 < activeScenarioList.length) {
                playSuccess();
                setScenarioIndex(i => i + 1);
                interactionStartTimeRef.current = Date.now();
                volatilityEventsRef.current = 0; // Reset for next iteration
            } else {
                setGameStatus('won');
                setBossMessage(gameData.winMessage || "Crisis navigated. Well done.");
                playWin();
            }
        } else {
            triggerFailure('eq_error', activeScenario.failureFeedback);
        }
        return;
    }

    // MEETING HERO & INTERVIEW MASTERY MODE
    if (id === 'game_meeting_hero' || id === 'game_interview_1' || id === 'game_behavioral_gauntlet') {
        const selected = draft[0];
        
        setSessionAttempts(prev => [...prev, { status: selected.category === 'Optimal' ? 'success' : 'failed', latencyMs: interactionLatency, selection: selected.text }]);

        if (selected.category === 'Optimal') {
            setHasFailedOnce(false);
            setShowHint(false);
            if (scenarioIndex + 1 < activeScenarioList.length) {
                playSuccess();
                setScenarioIndex(i => i + 1);
                interactionStartTimeRef.current = Date.now(); 
            } else {
                setGameStatus('won');
                setBossMessage(gameData.winMessage || "Meeting adjourned. Good work.");
                playWin();
            }
        } else {
            triggerFailure('meeting_error', selected.feedback);
        }
        return;
    }

    // LOOP MODE: The One-Touch Rule
    if (activeScenario.optimalMessageSequence) {
        const isCorrect = JSON.stringify(userSequence) === JSON.stringify(activeScenario.optimalMessageSequence);

        if (isCorrect) {
            setHasFailedOnce(false);
            setShowHint(false);
            if (scenarioIndex + 1 < activeScenarioList.length) {
                playSuccess();
                setScenarioIndex(i => i + 1);
                interactionStartTimeRef.current = Date.now();
            } else {
                setGameStatus('won');
                setBossMessage(gameData.winMessage || "Perfect execution.");
                playWin();
            }
        } else {
            triggerFailure('fragmented');
        }
        return;
    }

    // LEGACY MODE FALLBACK
    setGameStatus('won');
    setBossMessage(gameData.winMessage || "Mission Complete.");
    playWin();
  };

  const handleRetry = () => {
    // TELEMETRY PATCH: Push failure iteration to session history array
    setSessionAttempts(prev => [...prev, { 
        status: 'retry', 
        timestamp: Date.now(), 
        sequence: draft.map(d => d.text),
        volatilityCount: volatilityEventsRef.current || 0 
    }]);

    // Note: We deliberately do NOT reshuffle array during a retry loop 
    // so the user can apply the Coach's Hint to the exact scenario they failed.
    
    setPool(initialPool);
    setDraft([]);
    setGameStatus('playing');
    setBossMessage(activeScenario?.scenarioContext || activeScenario?.leaderPrompt || activeScenario?.adversaryPrompt || activeScenario?.incomingMessage || activeScenario?.initialMessage || "");
    setActiveIntervention(null);
    interactionStartTimeRef.current = Date.now();
    volatilityEventsRef.current = 0;
  };

  // PATCH: Chromium network suspension trap
  const handleFinalSubmit = async () => {
    const interactions = [
        ...sessionAttempts,
        { 
            status: 'success', 
            timestamp: Date.now(), 
            sequence: draft.map(d => d.text),
            volatilityCount: volatilityEventsRef.current || 0
        }
    ];
    
    if (typeof onComplete === 'function') {
        try {
            setIsSubmitting(true);
            await onComplete({ id, rawInteractions: interactions, durationMs: Date.now() - sessionStartRef.current });
        } catch (error) {
            console.error("Submission Failed:", error);
            if (error.message?.includes('network') || error.code === 'unavailable') {
                alert("Network connection suspended by the browser. Please refresh the page and try again.");
            } else {
                alert("An error occurred saving your data. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-100 rounded-xl overflow-hidden border border-gray-200 relative min-h-[500px]">
      
      {/* --- OVERLAY: BRIEFING --- */}
      {gameStatus === 'briefing' && preGameBriefing && (
          <TacticalOverlay 
            type="briefing" 
            data={preGameBriefing} 
            onContinue={() => {
                setGameStatus('playing');
                sessionStartRef.current = Date.now();
                interactionStartTimeRef.current = Date.now();
                volatilityEventsRef.current = 0;
            }} 
          />
      )}

      {/* --- OVERLAY: INTERVENTION (CHEAT CODE) --- */}
      {gameStatus === 'intervention' && activeIntervention && (
          <TacticalOverlay 
            type="intervention" 
            data={activeIntervention} 
            onRetry={handleRetry} 
          />
      )}

      {/* --- STANDARD HUD HEADER WITH BRIEFING RECALL --- */}
      <div className="bg-white p-4 border-b flex justify-between items-center shadow-sm z-10">
        <div className="flex items-center gap-3">
            <button 
                onClick={() => setGameStatus('briefing')} 
                className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500 transition-colors"
                title="Review Mission Briefing"
            >
                <HelpCircle size={16} />
            </button>
            <div className="hidden sm:flex flex-col">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active Session</span>
                <span className="text-sm font-bold text-gray-800">{bossName}</span>
            </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-xl hidden sm:flex">{bossAvatar}</div>
          <div className="flex flex-col items-end">
            {activeScenarioList.length > 1 && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-bold">
                  Scenario {scenarioIndex + 1}/{activeScenarioList.length}
              </span>
            )}
            {id === 'game_meeting_hero' && activeScenario?.meetingType && (
                <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">{activeScenario.meetingType}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex-grow p-4 overflow-y-auto bg-gray-50 flex flex-col gap-4">
        
        {/* Pre-Prompt Context for Interview Mastery Mode */}
        {activeScenario?.scenarioContext && (id === 'game_interview_1' || id === 'game_behavioral_gauntlet') && (
            <div className="self-center max-w-[95%] bg-indigo-50 border border-indigo-200 text-indigo-900 p-3 rounded-xl text-xs font-bold uppercase tracking-widest shadow-sm text-center mb-4">
               Context: {activeScenario.scenarioContext}
            </div>
        )}

        <div className={`self-start max-w-[85%] bg-white p-3 rounded-2xl rounded-tl-none border shadow-sm text-sm font-medium ${id === 'game_eq_builder' ? 'border-red-200 text-red-900 bg-red-50' : 'border-gray-200 text-gray-700'}`}>
          {bossMessage}
        </div>
        
        {(gameStatus === 'playing' || gameStatus === 'won') && draft.length > 0 && (
          <div className={`self-end max-w-[85%] p-3 rounded-2xl rounded-tr-none text-white text-sm shadow-sm ${gameStatus === 'won' ? 'bg-emerald-600' : 'bg-blue-600'}`}>
            {draft.map((d, idx) => (
                <div key={idx} className="mb-1">{d.text}</div>
            ))}
          </div>
        )}
        
        {gameStatus === 'won' && (
          <div className="mt-4 self-center w-full flex flex-col items-center gap-3">
              <button 
                  onClick={handleFinalSubmit} 
                  disabled={isSubmitting}
                  className="bg-green-600 text-white px-6 py-3 rounded-full font-bold shadow-lg hover:bg-green-700 flex items-center justify-center gap-2 animate-bounce disabled:opacity-50 disabled:animate-none"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                {isSubmitting ? 'Saving...' : 'Mission Complete'}
              </button>
          </div>
        )}
      </div>

      <div className="bg-white border-t p-4 z-20">
        <LayoutGroup>
          {/* Loop Mode / Fragment Assembly specific rendering */}
          {id === 'game_eq_builder' || id === 'game_slack_attack_v2' ? (
              <div className="min-h-[60px] bg-gray-100 rounded-xl p-2 mb-4 border-2 border-dashed border-gray-300 flex flex-wrap gap-2 items-center">
                <AnimatePresence>
                  {draft.map(item => (
                    <motion.button layoutId={item.id} key={item.id} onClick={() => handleMoveToPool(item)} className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1.5 rounded-lg border border-blue-200">
                      {item.text}
                    </motion.button>
                  ))}
                </AnimatePresence>
                <button onClick={handleSend} disabled={gameStatus !== 'playing' || draft.length === 0} className="ml-auto p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition">
                  <Send className="w-5 h-5" />
                </button>
              </div>
          ) : (
             <div className="hidden"></div>
          )}

          {/* The Options Grid (Candidate Replies) - Equal Height Enforcement */}
          <div className="w-full mt-auto">
              <AnimatePresence mode="wait">
                  <motion.div key={activeScenario?.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {pool.map((option, idx) => (
                          <button
                              key={idx}
                              disabled={gameStatus !== 'playing'}
                              onClick={() => {
                                  if (id === 'game_eq_builder' || id === 'game_slack_attack_v2') {
                                      handleMoveToDraft(option);
                                  } else {
                                      handleMoveToDraft(option);
                                      setTimeout(() => handleSend(), 10);
                                  }
                              }}
                              className={`w-full h-full flex items-center text-left p-4 rounded-2xl hover:shadow-md transition-all group disabled:opacity-50 disabled:cursor-not-allowed ${id === 'game_eq_builder' || id === 'game_slack_attack_v2' ? 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 text-xs font-medium' : 'bg-indigo-50 border-2 border-indigo-100 text-indigo-900 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'}`}
                          >
                              <span className="font-medium leading-relaxed text-sm w-full">
                                  {option.text || option.responseText}
                              </span>
                          </button>
                      ))}
                  </motion.div>
              </AnimatePresence>
          </div>
          
          {/* Coach's Hint Injection */}
          {hasFailedOnce && gameStatus === 'playing' && (
              <div className="mt-4 w-full">
                  <button 
                      onClick={() => setShowHint(!showHint)}
                      className="text-xs text-blue-600 font-bold uppercase tracking-widest hover:underline flex items-center gap-1"
                  >
                      <HelpCircle size={14} /> Request Coach's Hint
                  </button>
                  
                  {showHint && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 animate-fade-in text-left">
                          <span className="font-bold">Hint:</span> {activeScenario?.hintText || "You must address all questions asked, provide a timeline, and offer a specific next step. Do not use filler apologies."}
                      </div>
                  )}
              </div>
          )}

        </LayoutGroup>
      </div>
    </div>
  );
};

export default ChatHeroGame;